import { useState, useEffect } from 'react';

import Loading from "../../components/Loading";
import Data from '../../../repository';

const SECTIONS = [
	{ key: 'section2', label: 'Section 2: Standard Health Readiness (PAR-Q+)' },
	{ key: 'section3', label: 'Section 3: Solo / Unsupervised Training Risk Assessment' }
];

const cardStyle = { background: '#ffffff', padding: '40px', borderRadius: '0 0 6px 6px', marginTop: 0, width: '100%' };

function QuestionRow({ question, onSave, onDelete, onMove, isFirst, isLast, busy }) {
	const [editing, setEditing] = useState(false);
	const [text, setText] = useState(question.question);

	const save = async () => {
		if (!text.trim() || text === question.question) {
			setEditing(false);
			setText(question.question);
			return;
		}
		await onSave(text.trim());
		setEditing(false);
	};

	return (
		<div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
			{editing ? (
				<input
					type="text"
					style={{ flex: 1 }}
					value={text}
					disabled={busy}
					onChange={(e) => setText(e.target.value)}
					onBlur={save}
					onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } }}
					autoFocus
				/>
			) : (
				<p style={{ flex: 1, margin: 0, cursor: 'pointer' }} onClick={() => setEditing(true)}>{question.question}</p>
			)}
			<button type="button" className="btn" style={{ width: 'auto' }} disabled={busy || isFirst} onClick={() => onMove('up')}>↑</button>
			<button type="button" className="btn" style={{ width: 'auto' }} disabled={busy || isLast} onClick={() => onMove('down')}>↓</button>
			<button type="button" className="btn btnDanger" style={{ width: 'auto' }} disabled={busy} onClick={onDelete}>Delete</button>
		</div>
	);
}

function AddQuestionRow({ onAdd, busy }) {
	const [text, setText] = useState('');

	const submit = async (e) => {
		e.preventDefault();
		if (!text.trim()) return;
		await onAdd(text.trim());
		setText('');
	};

	return (
		<form style={{ display: 'flex', gap: '10px', marginTop: '14px' }} onSubmit={submit}>
			<input
				type="text"
				style={{ flex: 1 }}
				placeholder="Add a new question..."
				value={text}
				disabled={busy}
				onChange={(e) => setText(e.target.value)}
			/>
			<button type="submit" className="btn btnPrimary" style={{ width: 'auto' }} disabled={busy || !text.trim()}>Add Question</button>
		</form>
	);
}

function Questions() {
	const [loading, setLoading] = useState(true);
	const [questions, setQuestions] = useState({ section2: [], section3: [] });
	const [error, setError] = useState(null);
	const [busyId, setBusyId] = useState(null);

	useEffect(() => {
		refresh();
	}, []);

	const refresh = async () => {
		setLoading(true);
		try {
			const res = await Data.fetchHealthScreeningQuestions();
			setQuestions(res.data);
			setError(null);
		} catch (err) {
			setError('Unable to load questions. Please try again.');
		}
		setLoading(false);
	};

	const withBusy = async (id, action) => {
		setBusyId(id);
		try {
			await action();
			await refresh();
		} catch (err) {
			setError(err.response?.data?.message || 'Something went wrong. Please try again.');
		}
		setBusyId(null);
	};

	if (loading) return <Loading button={false} />;

	return (
		<div style={cardStyle}>
			{error && <p style={{ color: 'red', marginBottom: '10px' }}>{error}</p>}
			<p style={{ opacity: 0.7 }}>
				These are the Yes/No questions customers answer on the health screening form. Every business starts
				with the default PAR-Q+ questions below - add, edit, delete, or reorder them to suit your business.
			</p>

			{SECTIONS.map(({ key, label }) => {
				const sectionQuestions = questions[key] || [];
				return (
					<div key={key} style={{ marginTop: '30px' }}>
						<h2>{label}</h2>
						{!sectionQuestions.length && <p style={{ opacity: 0.7 }}>No questions in this section yet.</p>}
						{sectionQuestions.map((q, i) => (
							<QuestionRow
								key={q._id}
								question={q}
								busy={busyId === q._id}
								isFirst={i === 0}
								isLast={i === sectionQuestions.length - 1}
								onSave={(text) => withBusy(q._id, () => Data.updateHealthScreeningQuestion(q._id, { question: text }))}
								onDelete={() => withBusy(q._id, () => Data.deleteHealthScreeningQuestion(q._id))}
								onMove={(direction) => withBusy(q._id, () => Data.moveHealthScreeningQuestion(q._id, direction))}
							/>
						))}
						<AddQuestionRow
							busy={!!busyId}
							onAdd={(text) => withBusy('new', () => Data.createHealthScreeningQuestion({ section: key, question: text }))}
						/>
					</div>
				);
			})}
		</div>
	);
}

export default Questions;
